// Projects kept in the browser (IndexedDB). Each project is stored whole, with a small
// index entry for the project list. Where IndexedDB is unavailable (some private windows)
// projects live in memory for the session and the app says so. GPL-3.0-or-later.
import { sanitize, type Project } from "@dipfield/project";

export interface Entry { id: string; name: string; savedAt: string; planes: number; sets: number }
const DB = "dfsuite", VER = 1;
let dbp: Promise<IDBDatabase | null> | null = null;
const mem = { projects: new Map<string, Project>(), current: null as string | null };

function open(): Promise<IDBDatabase | null> {
  if (dbp) return dbp;
  dbp = new Promise((ok) => {
    try {
      if (typeof indexedDB === "undefined") return ok(null);
      const r = indexedDB.open(DB, VER);
      r.onupgradeneeded = () => {
        const d = r.result;
        if (!d.objectStoreNames.contains("projects")) d.createObjectStore("projects");
        if (!d.objectStoreNames.contains("index")) d.createObjectStore("index");
        if (!d.objectStoreNames.contains("kv")) d.createObjectStore("kv");
      };
      r.onsuccess = () => ok(r.result);
      r.onerror = () => ok(null);
      r.onblocked = () => ok(null);
    } catch { ok(null); }
  });
  return dbp;
}
export const available = async () => !!(await open());

function tx<T>(d: IDBDatabase, stores: string[], mode: IDBTransactionMode, f: (t: IDBTransaction) => IDBRequest<T> | void): Promise<T | undefined> {
  return new Promise((ok, fail) => {
    const t = d.transaction(stores, mode); let req: IDBRequest<T> | void;
    try { req = f(t); } catch (e) { fail(e); return; }
    t.oncomplete = () => ok(req ? req.result : undefined);
    t.onerror = () => fail(t.error); t.onabort = () => fail(t.error);
  });
}
const entry = (p: Project): Entry => ({ id: p.meta.id, name: p.meta.name, savedAt: p.meta.savedAt, planes: p.data.planes.length, sets: p.sets.length });

/** Store a project. Returns false if it could not be written (storage full or blocked). */
export async function saveProject(p: Project): Promise<boolean> {
  const d = await open();
  const copy = JSON.parse(JSON.stringify(p)) as Project;
  if (!d) { mem.projects.set(p.meta.id, copy); return true; }
  try {
    await tx(d, ["projects", "index"], "readwrite", (t) => { t.objectStore("projects").put(copy, p.meta.id); t.objectStore("index").put(entry(p), p.meta.id); });
    return true;
  } catch { return false; }
}
export async function loadProject(id: string): Promise<Project | null> {
  const d = await open();
  const raw = d ? await tx<unknown>(d, ["projects"], "readonly", (t) => t.objectStore("projects").get(id)).catch(() => undefined) : mem.projects.get(id);
  return raw ? sanitize(raw) : null;
}
export async function listProjects(): Promise<Entry[]> {
  const d = await open();
  const all = d ? ((await tx<Entry[]>(d, ["index"], "readonly", (t) => t.objectStore("index").getAll()).catch(() => [])) ?? []) : [...mem.projects.values()].map(entry);
  return all.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}
export async function deleteProject(id: string) {
  const d = await open();
  if (!d) { mem.projects.delete(id); return; }
  await tx(d, ["projects", "index"], "readwrite", (t) => { t.objectStore("projects").delete(id); t.objectStore("index").delete(id); }).catch(() => undefined);
}
export async function getCurrent(): Promise<string | null> {
  const d = await open();
  if (!d) return mem.current;
  return ((await tx<string>(d, ["kv"], "readonly", (t) => t.objectStore("kv").get("current")).catch(() => undefined)) ?? null);
}
export async function setCurrent(id: string) {
  const d = await open();
  if (!d) { mem.current = id; return; }
  await tx(d, ["kv"], "readwrite", (t) => { t.objectStore("kv").put(id, "current"); }).catch(() => undefined);
}
